import { GraviGrid, Horizontal } from '@gravitate-js/excalibrr'
import type { ColDef, GridApi } from 'ag-grid-community'
import dayjs from 'dayjs'
import { useMemo, useRef, type MutableRefObject } from 'react'

import type { Invoice, InvoiceGroup } from '../../../../api/useFreight/types'

import { ViewModeRadio } from './ViewModeRadio'
import { DownloadButton } from '../../../_SiteWide/Grid/ActionButtons/DownloadButton'

import { invoiceFlatColDefs } from './flatColDefs'

// Define a type that combines Invoice properties with additional properties from InvoiceGroup
export interface FlatInvoiceItem extends Invoice {
  group_number: string
  group_id: string
  book_type: 'Cost' | 'Revenue'
  counterparty_name: string
  start_date: string
  end_date: string
  distance_uom?: string
  base_distance?: number
}
export interface ExtendedColDef<T> extends ColDef<T> {
  excludeFromExport?: boolean
}

export function FlatInvoiceGrid({
  data,
  setSelectedViewStyle,
  selectedViewStyle,
  serverParams,
  setServerFilters,
  filters,
  costRevenue,
}: {
  data: InvoiceGroup[]
  setSelectedViewStyle: (viewStyle: 'grouped' | 'flat') => void
  selectedViewStyle: 'grouped' | 'flat'
  //@ts-ignore
  serverParams: Record<string, unknown>
  //@ts-ignore
  setServerFilters: (filters: Record<string, unknown>) => void
  //@ts-ignore
  filters: Record<string, unknown>
  costRevenue: string
}) {
  const gridAPIRef = useRef<GridApi<FlatInvoiceItem> | null>(null)
  // Add this minimal filter implementation to match ByGroupGrid behavior
  const filteredItems = useMemo(() => {
    // First validate that we have data
    if (!data) {
      return []
    }
    const items: FlatInvoiceItem[] = []
    // Then convert filtered groups to flat items
    for (const group of data) {
      // Skip any groups without invoices
      if (!group || !Array.isArray(group.invoices)) {
        continue
      }

      // Process all invoices in this group
      for (const invoice of group.invoices) {
        if (!invoice) {
          continue
        }

        // Create a flat item for this invoice
        items.push({
          ...invoice,
          group_number: group.group_number,
          group_id: group.id,
          book_type: group.book_type,
          counterparty_name: group.counterparty_name,
          start_date: group.start_date,
          end_date: group.end_date,
          // Add distance UOM from the first transaction if available
          distance_uom: invoice.transactions?.[0]?.distance_uom || '',
          // Ensure transactions is always an array, even if empty
          order_count: invoice.transactions?.length || 0,
          // Ensure transactions is always an array for downstream components
          transactions: invoice.transactions,
          // Add a unique ID if needed
          id: invoice.id || `flat-invoice-${group.id}-${group.invoices.indexOf(invoice)}`,
        } as FlatInvoiceItem)
      }
    }

    return items
  }, [data])

  // Replace flatItems with filteredItems here
  const flatItems = filteredItems

  return (
    <GraviGrid
      primaryKey='id'
      rowData={flatItems}
      externalRef={gridAPIRef as MutableRefObject<GridApi<FlatInvoiceItem>>}
      storageKey='freight-invoice-detail-expand'
      controlBarProps={{
        title: 'Freight Invoices',
        hideActiveFilters: true,
        actionButtons: (
          <Horizontal verticalCenter gap={8}>
            <ViewModeRadio selectedViewStyle={selectedViewStyle} setSelectedViewStyle={setSelectedViewStyle} />
            <DownloadButton fileName={`FreightInvoices_${dayjs().format('YYYY-MM-DD')}`} gridAPIRef={gridAPIRef} />
          </Horizontal>
        ),
        //@ts-ignore
        serverParams,
        //@ts-ignore
        setFilters: setServerFilters,
        //@ts-ignore
        filters,
      }}
      columnDefs={invoiceFlatColDefs}
      agPropOverrides={{
        headerHeight: 30,
        context: {
          groups: data,
          costRevenue,
        },
        rowHeight: 30,
        getRowId: (params) => params.data?.id || params.data?.invoice_number || `flat-invoice-${Math.random()}`,
        scrollbarWidth: 0,
        pagination: false,
        suppressPaginationPanel: true,
        rowGroupPanelShow: 'never',
        domLayout: 'normal',
      }}
    />
  )
}
